import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownload from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const CHART_COLORS = JSON.parse('["#1982c4", "#4267ac", "#6a4c93", "#b5a6c9", "#ff595e", "#ff924c", "#ffca3a", "#c5ca30", "#8ac926", "#52a675"]');

const UsersDashboard = (props) => {
  const { setLoading, loading } = props;

  const [users, setUsers] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    id: '',
    firstName: '',
    lastName: '',
    createdAt: '',
    updatedAt: '',
    email: '',
    country: '',
    platform: '',
    status: '',
    subscriptionType: '',
    isLogged: '',
    isLicenseActive: '',
    profileCompleted: '',
    profession: '',
    ageGroup: '',
    specialty: '',
    institution: ''
  });
  const [signupsOverTime, setSignupsOverTime] = useState([]);
  const [activeOverTime, setActiveOverTime] = useState([]);
  const [byAgeGroup, setByAgeGroup] = useState([]);
  const [byCountry, setByCountry] = useState([]);
  const [byProfession, setByProfession] = useState([]);
  const [subscriptionLookupStatus, setSubscriptionLookupStatus] = useState(null);

  // Simple per-column width state (all start equal, user can resize)
  const [columnWidths, setColumnWidths] = useState({
    firstName: 140,
    lastName: 140,
    email: 220,
    country: 120,
    createdAt: 180,
    platform: 120,
    subscriptionType: 200,
    isLicenseActive: 140,
    profileCompleted: 160,
    profession: 160,
    ageGroup: 140,
    specialty: 160,
    institution: 200,
  });

  const handleSearchChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportUsers = () => {
    const exportData = filteredUsers.map(u => ({
      ID: u.id || '',
      'First Name': u.firstName || '',
      'Last Name': u.lastName || '',
      Created: u.createdAt ? format(new Date(u.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      Updated: u.updatedAt ? format(new Date(u.updatedAt), 'yyyy-MM-dd HH:mm:ss') : '',
      Email: u.email || '',
      Country: u.country || '',
      Platform: u.platform || '',
      Status: u.subscriptionStatus || '',
      'Subscription Type': u.subscriptionType || '',
      'Is Logged': u.isLogged ? 'Yes' : 'No',
      'License Active': u.isLicenseActive ? 'Yes' : 'No',
      'Profile Completed': u.profileCompleted ? 'Yes' : 'No',
      Profession: u.profession || '',
      'Age Group': u.ageGroup || '',
      Specialty: u.specialty || '',
      Institution: u.institution || '',
    }));
    exportToExcel(exportData, `users_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Prefer explicit endpoint if set, otherwise fall back to provided users URL
      let baseUrl;
      if (process.env.REACT_APP_API_USERS_ENDPOINT) {
        baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        baseUrl = `${baseUrl}${process.env.REACT_APP_API_USERS_ENDPOINT}`;
      } else {
        baseUrl = 'https://anesthesiaassistant-be.onrender.com/api/v1/users/';
      }
      
      // Fetch all users using pagination
      const allUsers = [];
      const limit = 100;
      let skip = 0;
      let hasMore = true;
      
      while (hasMore) {
        // Handle URL construction - check if baseUrl already has query parameters
        const separator = baseUrl.includes('?') ? '&' : '?';
        const url = `${baseUrl}${separator}skip=${skip}&limit=${limit}`;
        const res = await fetch(url);
        const data = await res.json();
        const list = Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : []);
        
        if (list.length === 0) {
          hasMore = false;
        } else {
          allUsers.push(...list);
          // If we got fewer results than the limit, we've reached the end
          if (list.length < limit) {
            hasMore = false;
          } else {
            skip += limit;
          }
        }
      }
      
      // Anesthesia Assistant appId as fallback
      const ANESTHESIA_ASSISTANT_APP_ID = '674997d8c4aaadc4054b3372';
      
      const normalized = allUsers.map((u) => ({
        id: u.id,
        iaphub_id: u.iaphub_user_identity,
        appId: u.app_id || u.appId || ANESTHESIA_ASSISTANT_APP_ID,
        iaphub_user_identity: u.iaphub_user_identity || '',
        firstName: u.first_name || '',
        lastName: u.last_name || '',
        createdAt: u.created_at || u.createdAt || null,
        updatedAt: u.updated_at || null,
        email: u.email || '',
        country: u.country || '',
        platform: u.provider || u.platform || '',
        isLogged: u.is_logged === true,
        isLicenseActive: !!u.is_license_active,
        profileCompleted: !!u.profile_completed,
        profession: u.profession || '',
        ageGroup: u.age_group || '',
        subscriptionStatus: (u.is_license_active === true || (typeof u.subscriptionStatus === 'string' && u.subscriptionStatus)) 
          ? (u.is_license_active ? 'Active' : (u.subscriptionStatus || 'Inactive')) 
          : '',
        subscriptionType: '',
        activeProducts: [],
        lastActiveAt: u.is_logged_at || u.updated_at || u.lastActiveAt || null,
        specialty: u.specialty || '',
        institution: u.institution || '',
      }));
      setUsers(normalized);
      setLastRefreshed(new Date());
      
      await fetchUserSubscriptions(normalized);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error fetching users from server');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscriptions = async (userList) => {
    if (!Array.isArray(userList) || userList.length === 0) {
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const endpoint = '/user/subscriptions/cached';
    
    if (!baseUrl) {
      console.warn('Subscription lookup skipped: API base URL not configured.');
      return;
    }

    setSubscriptionLookupStatus({ completed: 0, total: userList.length });

    try {
      const response = await fetch(`${baseUrl}${endpoint}`);
      if (!response.ok) {
        console.error(`Failed to fetch cached subscriptions: ${response.status} ${response.statusText}`);
        setSubscriptionLookupStatus(null);
        return;
      }
      
      const data = await response.json();
      const cachedSubscriptions = data.subscriptions || {};
      
      // Merge cached subscription data with users
      setUsers((prev) =>
        prev.map((user) => {
          const cacheKey = user.id?.toString();
          const cached = cachedSubscriptions[cacheKey];
          console.log(cached);
          if (cached) {
            return {
              ...user,
              subscriptionType: cached.activeProducts.length > 0 && cached.activeProducts[0].sku ? 
              cached.activeProducts[0].sku == 'au_yearly_premium' ? 'Yearly' : 'Monthly' : '',
              activeProducts: Array.isArray(cached.activeProducts) ? cached.activeProducts : []
            };
          }
          
          return user;
        })
      );
      
      setSubscriptionLookupStatus({ completed: userList.length, total: userList.length });
      
      // Clear status after a brief delay
      setTimeout(() => {
        setSubscriptionLookupStatus(null);
      }, 500);
    } catch (error) {
      console.error('Error fetching cached subscriptions:', error);
      setSubscriptionLookupStatus(null);
    }
  };

  useEffect(() => {
      fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate metrics and chart datasets when users change
  useEffect(() => {
    if (!users || users.length === 0) {
      setSignupsOverTime([]);
      setActiveOverTime([]);
      setByAgeGroup([]);
      setByCountry([]);
      setByProfession([]);
      return;
    }
    // Signups over time by month
    const monthly = {};
    const monthlyActive = {};
    users.forEach(u => {
      if (!u.createdAt) return;
      const d = new Date(u.createdAt);
      if (isNaN(d.getTime())) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      monthly[key] = (monthly[key] || 0) + 1;
      if (u.isLicenseActive) {
        monthlyActive[key] = (monthlyActive[key] || 0) + 1;
      }
    });
    const allMonths = Array.from(new Set([...Object.keys(monthly), ...Object.keys(monthlyActive)])).sort();
    setSignupsOverTime(allMonths.map(m => ({ month: m, signups: monthly[m] || 0 })));
    setActiveOverTime(allMonths.map(m => ({ month: m, activeLicenses: monthlyActive[m] || 0 })));
    // By age group
    const ageGroupCounts = {};
    users.forEach(u => {
      const ag = u.ageGroup;
      if (ag && ag.trim() !== '') {
        ageGroupCounts[ag] = (ageGroupCounts[ag] || 0) + 1;
      }
    });
    const allAgeGroups = Object.entries(ageGroupCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalAgeGroups = allAgeGroups.reduce((sum, item) => sum + item.value, 0);
    setByAgeGroup(allAgeGroups.sort((a,b)=>b.value-a.value).map(item => ({ ...item, percent: totalAgeGroups > 0 ? (item.value / totalAgeGroups) * 100 : 0 })));
    // By country
    const countryCounts = {};
    users.forEach(u => {
      const c = u.country;
      if (c && c.trim() !== '') {
        countryCounts[c] = (countryCounts[c] || 0) + 1;
      }
    });
    const allCountries = Object.entries(countryCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalCountries = allCountries.reduce((sum, item) => sum + item.value, 0);
    setByCountry(allCountries.sort((a,b)=>b.value-a.value).slice(0,5).map(item => ({ ...item, percent: totalCountries > 0 ? (item.value / totalCountries) * 100 : 0 })));
    // By profession
    const professionCounts = {};
    users.forEach(u => {
      const p = u.profession;
      if (p && p.trim() !== '') {
        professionCounts[p] = (professionCounts[p] || 0) + 1;
      }
    });
    const allProfessions = Object.entries(professionCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalProfessions = allProfessions.reduce((sum, item) => sum + item.value, 0);
    setByProfession(allProfessions.sort((a,b)=>b.value-a.value).slice(0,5).map(item => ({ ...item, percent: totalProfessions > 0 ? (item.value / totalProfessions) * 100 : 0 })));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const filtered = users.filter(u => {
      const id = (u.id || '').toString().toLowerCase();
      const firstName = (u.firstName || '').toLowerCase();
      const lastName = (u.lastName || '').toLowerCase();
      const createdAt = (u.createdAt || '').toString().toLowerCase();
      const updatedAt = (u.updatedAt || '').toString().toLowerCase();
      const email = (u.email || '').toLowerCase();
      const country = (u.country || '').toLowerCase();
      const platform = (u.platform || '').toLowerCase();
      const status = (u.subscriptionStatus || '').toLowerCase();
      const subscriptionType = (u.subscriptionType || '').toLowerCase();
      const profession = (u.profession || '').toLowerCase();
      const ageGroup = (u.ageGroup || '').toLowerCase();
      const specialty = (u.specialty || '').toLowerCase();
      const institution = (u.institution || '').toLowerCase();
      const isLoggedMatch = filters.isLogged === '' ? true : (filters.isLogged === 'yes' ? u.isLogged === true : u.isLogged === false);
      const isLicenseActiveMatch = filters.isLicenseActive === '' ? true : (filters.isLicenseActive === 'yes' ? u.isLicenseActive === true : u.isLicenseActive === false);
      const profileCompletedMatch = filters.profileCompleted === '' ? true : (filters.profileCompleted === 'yes' ? u.profileCompleted === true : u.profileCompleted === false);

      // Subscription type filter:
      // - ''           -> All users (no additional filtering)
      // - 'subscribed' -> users with any subscription (Monthly or Yearly)
      // - 'monthly'    -> users with Monthly subscription
      // - 'yearly'     -> users with Yearly subscription
      // - 'none'       -> Non-subscribers (no active subscription type)
      let subscriptionTypeMatch = true;
      if (filters.subscriptionType === 'subscribed') {
        subscriptionTypeMatch = subscriptionType !== '';
      } else if (filters.subscriptionType === 'monthly') {
        subscriptionTypeMatch = subscriptionType === 'monthly';
      } else if (filters.subscriptionType === 'yearly') {
        subscriptionTypeMatch = subscriptionType === 'yearly';
      } else if (filters.subscriptionType === 'none') {
        subscriptionTypeMatch = subscriptionType === '';
      }

      return (
        id.includes(filters.id.toLowerCase()) &&
        firstName.includes(filters.firstName.toLowerCase()) &&
        lastName.includes(filters.lastName.toLowerCase()) &&
        createdAt.includes(filters.createdAt.toLowerCase()) &&
        updatedAt.includes(filters.updatedAt.toLowerCase()) &&
        email.includes(filters.email.toLowerCase()) &&
        country.includes(filters.country.toLowerCase()) &&
        platform.includes(filters.platform.toLowerCase()) &&
        status.includes(filters.status.toLowerCase()) &&
        subscriptionTypeMatch &&
        isLoggedMatch &&
        isLicenseActiveMatch &&
        profileCompletedMatch &&
        profession.includes(filters.profession.toLowerCase()) &&
        ageGroup.includes(filters.ageGroup.toLowerCase()) &&
        specialty.includes(filters.specialty.toLowerCase()) &&
        institution.includes(filters.institution.toLowerCase())
      );
    });
    
    // Sort by creation date (newest first)
    return filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });
  }, [users, filters]);

  const paginated = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const activeUsers = users.filter(u => u.isLogged === true).length;
  const totalUsers = users.length;
  const newUsers90 = users.filter(u => {
    if (!u.createdAt) return false;
    const created = new Date(u.createdAt);
    if (isNaN(created.getTime())) return false;
    return Date.now() - created.getTime() <= 90 * 24 * 60 * 60 * 1000;
  }).length;
  const profileCompletedCount = users.filter(u => u.profileCompleted).length;
  const profileCompletedRate = totalUsers > 0 ? Math.round((profileCompletedCount / totalUsers) * 100) : 0;
  const deactivationsCount = users.filter(u => !u.isLicenseActive).length;
  const deactivatedAccounts = users.filter(u => !u.isLicenseActive && !u.isLogged).length;
  
  // Calculate subscribed users (with active subscriptions)
  const subscribedUsers = users.filter(u => u.subscriptionType && u.subscriptionType !== null && u.subscriptionType !== '').length;
  const subscriptionRate = totalUsers > 0 ? Math.round((subscribedUsers / totalUsers) * 100) : 0;

  const uniquePlatforms = Array.from(new Set(users.map(u => u.platform).filter(Boolean)));
  const uniqueStatuses = Array.from(new Set(users.map(u => u.subscriptionStatus).filter(Boolean)));
  const uniqueProfessions = Array.from(new Set(users.map(u => u.profession).filter(Boolean)));
  const uniqueAgeGroups = Array.from(new Set(users.map(u => u.ageGroup).filter(Boolean)));

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" color="#1982c4">
          Anesthesia Assistant Users
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 2, justifyContent: 'center' }}>
            <Grid item xs={12} md={3} lg={3}>
              <Card sx={{ height: '100%', backgroundColor: '#daf0ff' }}>
                <CardContent>
                  <Typography gutterBottom>Total Users</Typography>
                  <Typography variant="h4" color="primary" sx={{ textAlign: 'center' }}>{totalUsers}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Card sx={{ height: '100%', backgroundColor: '#daf0ff' }}>
                <CardContent>
                  <Typography gutterBottom>Subscription Rate</Typography>
                  <Typography variant="h4" color="primary" sx={{ textAlign: 'center' }}>{subscriptionRate}%</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                    {subscribedUsers} of {totalUsers} subscribed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Card sx={{ height: '100%', backgroundColor: '#daf0ff' }}>
                <CardContent>
                  <Typography gutterBottom>Profile Completion Rate</Typography>
                  <Typography variant="h4" color="primary" sx={{ textAlign: 'center' }}>{profileCompletedRate}%</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                    {profileCompletedCount} of {totalUsers} completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Card sx={{ height: '100%', backgroundColor: '#daf0ff' }}>
                <CardContent>
                  <Typography gutterBottom>Deactivated Accounts</Typography>
                  <Typography variant="h4" color="primary" sx={{ textAlign: 'center' }}>{deactivatedAccounts}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3, justifyContent: 'center' }}>
            <Grid item xs={12} md={4} sx={{ width: '30%' }}>
              <Card sx={{ width: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Users by Age Group</Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie 
                        data={byAgeGroup} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={90} 
                        label={(entry) => {
                          const dataEntry = byAgeGroup.find(item => item.name === entry.name);
                          return dataEntry ? `${dataEntry.percent.toFixed(1)}%` : '';
                        }}
                      >
                        {byAgeGroup.map((entry, index) => (
                          <Cell key={`age-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Count']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} sx={{ width: '30%' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Top 5 Countries</Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie 
                        data={byCountry} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={90} 
                        label={(entry) => {
                          const dataEntry = byCountry.find(item => item.name === entry.name);
                          return dataEntry ? `${dataEntry.percent.toFixed(1)}%` : '';
                        }}
                      >
                        {byCountry.map((entry, index) => (
                          <Cell key={`country-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Count']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} sx={{ width: '30%' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Users by Profession</Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie 
                        data={byProfession} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={90} 
                        label={(entry) => {
                          const dataEntry = byProfession.find(item => item.name === entry.name);
                          return dataEntry ? `${dataEntry.percent.toFixed(1)}%` : '';
                        }}
                      >
                        {byProfession.map((entry, index) => (
                          <Cell key={`prof-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Count']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Table of Users
              </Typography>
              <Button sx={{ backgroundColor: '#daf0ff', color: 'black' }} variant="contained" onClick={exportUsers} startIcon={<FileDownload color="black"/>}>Export Users</Button>
            </Box>
            {subscriptionLookupStatus && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Fetching subscription data: {subscriptionLookupStatus.completed}/{subscriptionLookupStatus.total}
                </Typography>
              </Box>
            )}
            <TableContainer component={Paper}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #e0e0e0',
                  },
                  '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search First Name"
                        value={filters.firstName}
                        onChange={(e) => handleSearchChange('firstName', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Last Name"
                        value={filters.lastName}
                        onChange={(e) => handleSearchChange('lastName', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Email"
                        value={filters.email}
                        onChange={(e) => handleSearchChange('email', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Country"
                        value={filters.country}
                        onChange={(e) => handleSearchChange('country', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Created At"
                        value={filters.createdAt}
                        onChange={(e) => handleSearchChange('createdAt', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel>Subscription Type</InputLabel>
                        <Select
                          value={filters.subscriptionType}
                          sx={{ mb: 2 }}
                          onChange={(e) => handleSearchChange('subscriptionType', e.target.value)}
                          label="Subscription Type"
                        >
                          <MenuItem value="">All users</MenuItem>
                          <MenuItem value="subscribed">Any subscriptions</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                          <MenuItem value="none">Non-subscribers</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel>License Active</InputLabel>
                        <Select
                          value={filters.isLicenseActive}
                          sx={{ mb: 2 }}
                          onChange={(e) => handleSearchChange('isLicenseActive', e.target.value)}
                          label="License Active"
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel>Profile Completed</InputLabel>
                        <Select
                          value={filters.profileCompleted}
                          sx={{ mb: 2 }}
                          onChange={(e) => handleSearchChange('profileCompleted', e.target.value)}
                          label="Profile Completed"
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel>Profession</InputLabel>
                        <Select
                          value={filters.profession}
                          sx={{ mb: 2 }}
                          onChange={(e) => handleSearchChange('profession', e.target.value)}
                          label="Profession"
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueProfessions.map((p) => (
                            <MenuItem key={p} value={p?.toLowerCase()}>{p}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel>Age Group</InputLabel>
                        <Select
                          value={filters.ageGroup}
                          sx={{ mb: 2 }}
                          onChange={(e) => handleSearchChange('ageGroup', e.target.value)}
                          label="Age Group"
                        >
                          <MenuItem value="">All</MenuItem>
                          {uniqueAgeGroups.map((group) => (
                            <MenuItem key={group} value={group?.toLowerCase()}>{group}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Specialty"
                        value={filters.specialty}
                        onChange={(e) => handleSearchChange('specialty', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Search Institution"
                        value={filters.institution}
                        onChange={(e) => handleSearchChange('institution', e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.firstName }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>First Name</Box>
                        <Box
                          sx={{
                            width: 4,
                            ml: 0.5,
                            alignSelf: 'stretch',
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.lastName }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Last Name</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.email }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Email</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.country }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Country</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.createdAt }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Created At</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.subscriptionType }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Subscription Type</Box>
                        <Box
                          sx={{ width: 6, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.isLicenseActive }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>License Active</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.profileCompleted }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Profile Completed</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.profession }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Profession</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.ageGroup }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Age Group</Box>
                        <Box
                          sx={{ width: 3, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.specialty }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Specialty</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: columnWidths.institution }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>Institution</Box>
                        <Box
                          sx={{ width: 4, ml: 0.5, alignSelf: 'stretch' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((u, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{u.firstName}</TableCell>
                      <TableCell>{u.lastName}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: columnWidths.email,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={u.email || ''}
                      >
                        {u.email}
                      </TableCell>
                      <TableCell>{u.country}</TableCell>
                      <TableCell>{u.createdAt ? format(new Date(u.createdAt), 'yyyy-MM-dd HH:mm:ss') : ''}</TableCell>
                      <TableCell>{u.subscriptionType}</TableCell>
                      <TableCell>{u.isLicenseActive ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.profileCompleted ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.profession}</TableCell>
                      <TableCell>{u.ageGroup}</TableCell>
                      <TableCell>{u.specialty}</TableCell>
                      <TableCell>{u.institution}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
          </Paper>
        </>
      )}
    </Box>
    </>
  );
};

export default UsersDashboard;


